/**
 * El cartel del GOL.
 *
 * Vive en su propio módulo y no dentro de `main.ts` por una razón práctica:
 * `main.ts` arranca el juego al importarlo, así que nada podía mirar esta
 * animación sin abrir una partida y marcar un gol a mano. Aquí se puede pintar
 * en un banco (`bench/goal.html`) y avanzarla fotograma a fotograma, que es la
 * única forma sensata de ajustar una animación.
 *
 * Es una función PURA de dibujo: recibe el instante y pinta. No guarda estado,
 * no lee ajustes y no sabe qué es una partida.
 */

/**
 * Curvas de suavizado. Aburridas, y por eso están con nombre: `1 - (1-x)^3`
 * repetido cuatro veces en línea es donde se cuela un exponente distinto sin que
 * nadie lo note.
 */
export const easeOut = (x: number): number => 1 - Math.pow(1 - x, 3);
export const easeIn = (x: number): number => x * x * x;
/** 0 fuera del tramo `[a, b]`, 0..1 dentro. Recorta la línea de tiempo. */
export const tramo = (x: number, a: number, b: number): number =>
  x <= a ? 0 : x >= b ? 1 : (x - a) / (b - a);

/** A partir de aquí (en progreso 0..1) el cartel empieza a apagarse. */
export const GOAL_HOLD = 0.72;

/**
 * Opacidad general del cartel en el instante `t` (1 al empezar, 0 al acabar).
 *
 * Es EL arreglo de la animación, y por eso está aparte y con prueba. La versión
 * anterior usaba `t` tal cual, que es una rampa descendente desde el primer
 * fotograma: el cartel empezaba a irse antes de haber llegado, y nunca había un
 * instante en que estuviera puesto. Ahora se mantiene encendido hasta
 * `GOAL_HOLD` y solo entonces se apaga, con una entrada suave (`easeIn`) para
 * que el final no sea un corte.
 */
export function goalEnvelope(t: number): number {
  const p = 1 - t;
  return 1 - easeIn(tramo(p, GOAL_HOLD, 1));
}

/**
 * Animación de gol: el láser sale DE LA PORTERÍA en la que entró y escribe GOL.
 *
 * ## Por qué se rehízo
 *
 * La versión anterior funcionaba pero contaba otra historia. El láser crecía
 * simétrico desde el centro de la pantalla, sin relación con dónde había pasado
 * nada, y todo —brillo, palabra, fondo— se desvanecía linealmente con `t` desde
 * el primer frame. Eso da una animación que empieza a irse antes de haber
 * llegado: nunca hay un instante en que esté "puesta".
 *
 * Ahora hay una línea de tiempo con tramos, y el láser tiene ORIGEN. Sale del
 * lado por el que entró el balón —el mismo sitio donde acaba de reventar la
 * explosión de la cancha— y barre la pantalla hasta el centro, donde aterriza la
 * palabra. Las dos cosas cuentan lo mismo: el cartel es la continuación del
 * golpe, no un rótulo pegado encima.
 *
 * ## La línea de tiempo
 *
 * `p` va de 0 (empieza) a 1 (termina), al revés que `t`, porque pensar una
 * animación hacia atrás es cómo se cuelan los errores de fase.
 *
 *   0,00 – 0,20  el láser barre desde la portería hasta el centro
 *   0,08 – 0,34  la palabra entra con un rebote corto
 *   0,00 – 0,72  todo se mantiene ENCENDIDO (esto es lo que faltaba)
 *   0,72 – 1,00  se apaga, y el láser se estrecha hasta desaparecer
 *
 * `desde` es -1 si el balón entró por la izquierda y +1 por la derecha. Es
 * `goalSide` traducido: cruzar por la izquierda es gol del equipo 1.
 *
 * `calm` (ACCESO · reducir destellos) no la quita —el gol tiene que anunciarse—
 * sino que le baja el brillo, el barrido y el rebote.
 */
export function drawGoalAnim(
  ctx: CanvasRenderingContext2D, t: number, color: string,
  vw: number, vh: number, calm = false, desde: -1 | 1 = 1,
): void {
  const cy = vh / 2;
  const cx = vw / 2;
  const flash = calm ? 0.35 : 1;
  const p = 1 - t;

  const vida = goalEnvelope(t);

  // Fondo oscurecido, con la misma envolvente que el resto.
  ctx.fillStyle = `rgba(3,6,10,${(0.62 * vida).toFixed(3)})`;
  ctx.fillRect(0, 0, vw, vh);

  // --- el láser ---
  // La cabeza viaja desde el borde de la portería hasta el centro; la cola se
  // queda atrás un poco más, así que el haz se estira al salir y se recoge al
  // llegar. Un segmento de largo fijo desplazándose parecería un objeto que se
  // mueve; esto parece un disparo.
  const barrido = easeOut(tramo(p, 0, calm ? 0.26 : 0.2));
  const cola = easeOut(tramo(p, 0, calm ? 0.4 : 0.34));
  const borde = desde > 0 ? vw : 0;
  const cabezaX = borde + (cx - borde) * barrido;
  const colaX = borde + (cx - borde) * cola;
  // Pasado el barrido, el haz se abre al ancho completo hacia los DOS lados: el
  // gol ocupa la pantalla, no solo la mitad por la que vino.
  const apertura = easeOut(tramo(p, calm ? 0.26 : 0.2, 0.45));
  const semi = (cx + 60) * apertura;
  const izq = Math.min(cabezaX, colaX, cx - semi);
  const der = Math.max(cabezaX, colaX, cx + semi);

  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const grosor = (calm ? 4 : 7) + (calm ? 6 : 20) * vida * flash;
  const grad = ctx.createLinearGradient(izq, 0, der, 0);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(0.5, color);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.shadowColor = color;
  ctx.shadowBlur = (26 * vida + 8) * flash;
  ctx.globalAlpha = 0.85 * vida * flash;
  ctx.fillStyle = grad;
  ctx.fillRect(izq, cy - grosor / 2, der - izq, grosor);
  // Núcleo fino y brillante. Se estrecha al final, que es lo que hace que el
  // láser se APAGUE en vez de simplemente volverse transparente.
  ctx.shadowBlur = 0;
  ctx.globalAlpha = vida * flash;
  const nucleo = ctx.createLinearGradient(izq, 0, der, 0);
  nucleo.addColorStop(0, "rgba(255,255,255,0)");
  nucleo.addColorStop(0.5, `rgba(255,255,255,${(0.9 * vida * flash).toFixed(3)})`);
  nucleo.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = nucleo;
  const finoH = Math.max(0.5, 3 * vida);
  ctx.fillRect(izq, cy - finoH / 2, der - izq, finoH);
  ctx.restore();

  // --- la palabra ---
  // Entra un poco después que el láser: primero llega el disparo, luego el
  // rótulo. Con las dos a la vez, la palabra tapaba su propia entrada.
  const entrada = tramo(p, 0.08, calm ? 0.3 : 0.34);
  if (entrada > 0) {
    // Rebote corto: se pasa de tamaño y vuelve. El anterior se pasaba un 100 %
    // y daba un golpe seco; este se pasa un 12 % y se asienta.
    const e = easeOut(entrada);
    const rebote = calm ? 0 : Math.sin(entrada * Math.PI) * 0.12;
    const escala = 0.72 + 0.28 * e + rebote;
    // Llega arrastrada desde el lado del disparo, poco: lo justo para que la
    // palabra parezca traída por el láser y no aparecida encima de él.
    const arrastre = desde * (1 - e) * (calm ? 12 : 46);
    ctx.save();
    ctx.translate(cx + arrastre, cy);
    ctx.scale(escala, escala);
    ctx.globalAlpha = vida * Math.min(1, entrada * 2);
    ctx.shadowColor = color;
    ctx.shadowBlur = 18 * vida * flash;
    ctx.fillStyle = color;
    ctx.font = "900 88px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("GOL", 0, 0);
    ctx.restore();
    ctx.globalAlpha = 1;
    ctx.textAlign = "start";
    ctx.textBaseline = "top";
  }
}
